/**
 * Listens for Word document selection changes and exposes the currently
 * selected text.
 *
 * Fires whenever the user moves the cursor or changes the selection in Word.
 * Falls back to a manual "read selection" helper when Office is unavailable
 * (browser preview mode).
 */

import { useCallback, useEffect, useRef } from 'react';

const DEBOUNCE_MS = 300;

type UseWordSelectionOptions = {
  setSelectedText: (text: string | null) => void;
  enabled?: boolean;
  /** When this value changes to `'idle'`, re-read the current selection. */
  refreshTrigger?: unknown;
};

export function useWordSelection({
  setSelectedText,
  enabled = true,
  refreshTrigger,
}: UseWordSelectionOptions): { readSelection: () => Promise<void> } {
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevRefreshTrigger = useRef(refreshTrigger);

  const readCurrentSelection = useCallback(async () => {
    if (!enabled) return;

    if (typeof Word === 'undefined' || !Word.run) {
      // Browser preview — no-op
      return;
    }

    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load('text');
        await context.sync();

        const trimmed = selection.text?.trim() ?? '';
        setSelectedText(trimmed.length > 0 ? trimmed : null);
      });
    } catch {
      // Ignore transient Office.js errors (e.g. during rapid selection changes)
    }
  }, [enabled, setSelectedText]);

  const debouncedRead = useCallback(() => {
    if (!enabled) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      void readCurrentSelection();
    }, DEBOUNCE_MS);
  }, [enabled, readCurrentSelection]);

  useEffect(() => {
    if (!enabled) return;
    void readCurrentSelection();
  }, [enabled, readCurrentSelection]);

  useEffect(() => {
    if (typeof Office === 'undefined' || !enabled) return;

    const handler = () => debouncedRead();

    try {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        handler,
      );
    } catch {
      // Not fatal — the user can still trigger a read manually
    }

    return () => {
      try {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler },
          () => {/* ignore cleanup errors */},
        );
      } catch {
        // ignore
      }
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [debouncedRead, enabled]);

  useEffect(() => {
    if (!enabled || refreshTrigger === undefined) return;
    if (refreshTrigger === 'idle' && prevRefreshTrigger.current !== 'idle') {
      void readCurrentSelection();
    }
    prevRefreshTrigger.current = refreshTrigger;
  }, [enabled, refreshTrigger, readCurrentSelection]);

  return { readSelection: readCurrentSelection };
}
